import React, { useState, useMemo, useContext, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import useQuery from 'components/UseQuery'
import { GET_CURRENT_CONVENTION, GET_CONVENTIONS, GET_CONVENTION_SERIES } from 'queries/conventions'
import { applyThemeToDom } from 'utils/theme'

const CurrentConventionContext = React.createContext({})

const useCurrentConvention = () => useContext(CurrentConventionContext).convention

function allowDevOverrides() {
	return process.env.ENV === 'development'
}

function safeLocalStorageGetItem(key) {
	if (typeof window === 'undefined') return null

	try {
		return window.localStorage?.getItem(key) || null
	} catch (_e) {
		return null
	}
}

function safeLocalStorageSetItem(key, value) {
	if (typeof window === 'undefined') return

	try {
		window.localStorage?.setItem(key, value)
	} catch (_e) {
		return
	}
}

function getDevOverrideConventionId() {
	if (typeof window === 'undefined') return null
	if (!allowDevOverrides()) return null

	const params = new URLSearchParams(window.location.search)
	return params.get('convention_id')
}

function getDevOverrideSeriesId() {
	if (typeof window === 'undefined') return null
	if (!allowDevOverrides()) return null

	const params = new URLSearchParams(window.location.search)
	return params.get('series_id')
}

function pickDefaultConventionId(conventions) {
	if (!Array.isArray(conventions) || conventions.length === 0) return null

	const now = new Date()
	const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))

	const parsed = conventions
		.map((c) => {
			const start = c?.start_date ? new Date(`${c.start_date}T00:00:00Z`) : null
			const end = c?.end_date ? new Date(`${c.end_date}T00:00:00Z`) : null
			return { c, start, end }
		})
		.filter(({ c }) => c?.id)

	const running = parsed.find(({ start, end }) => start && end && start <= today && end >= today)
	if (running?.c?.id) return running.c.id

	const upcoming = parsed.filter(({ start }) => start && start > today).sort((a, b) => a.start - b.start)[0]
	if (upcoming?.c?.id) return upcoming.c.id

	const past = parsed.filter(({ end }) => end && end < today).sort((a, b) => b.end - a.end)[0]
	if (past?.c?.id) return past.c.id

	return parsed[0]?.c?.id || null
}

const CurrentConventionProvider = ({ children }) => {
	const allow_dev_overrides = allowDevOverrides()
	const storage_key = 'portal_active_convention_id'
	const series_storage_key = 'portal_active_series_id'

	const {
		data: { current_convention },
		loading: loading_current,
	} = useQuery(GET_CURRENT_CONVENTION, {
		default_data: {
			current_convention: {},
		},
	})

	const domain_series_id = current_convention?.convention_series_id || current_convention?.convention_series?.id

	const {
		data: { convention_series },
		loading: loading_series,
	} = useQuery(GET_CONVENTION_SERIES, {
		default_data: { convention_series: [] },
	})

	const override_series_id = useMemo(() => getDevOverrideSeriesId(), [])
	const stored_series_id = useMemo(() => safeLocalStorageGetItem(series_storage_key), [series_storage_key])
	const [selected_series_id, setSelectedSeriesId] = useState('')

	const preferred_series_id = selected_series_id || override_series_id || stored_series_id || domain_series_id || ''

	const active_series_id = useMemo(() => {
		if (!preferred_series_id) return ''

		if (!domain_series_id) return preferred_series_id
		if (!Array.isArray(convention_series) || convention_series.length === 0) return preferred_series_id

		const exists = convention_series.some((s) => s?.id === preferred_series_id)
		return exists ? preferred_series_id : domain_series_id
	}, [preferred_series_id, domain_series_id, convention_series])

	useEffect(() => {
		if (!allow_dev_overrides) return
		if (!active_series_id) return

		safeLocalStorageSetItem(series_storage_key, active_series_id)
	}, [allow_dev_overrides, active_series_id, series_storage_key])

	const {
		data: { conventions },
		loading: loading_conventions,
	} = useQuery(GET_CONVENTIONS, {
		variables: {
			series_id: active_series_id,
		},
		default_data: {
			conventions: [],
		},
		skip: !active_series_id,
	})

	const override_convention_id = useMemo(() => getDevOverrideConventionId(), [])
	const stored_convention_id = useMemo(() => safeLocalStorageGetItem(storage_key), [storage_key])
	const [selected_convention_id, setSelectedConventionId] = useState('')

	const preferred_convention_id =
		selected_convention_id || override_convention_id || stored_convention_id || current_convention?.id || ''

	const active_convention_id = useMemo(() => {
		if (!preferred_convention_id) return ''
		if (!Array.isArray(conventions) || conventions.length === 0) return preferred_convention_id

		const exists_in_series = conventions.some((c) => c?.id === preferred_convention_id)
		if (exists_in_series) return preferred_convention_id

		return pickDefaultConventionId(conventions) || preferred_convention_id
	}, [preferred_convention_id, conventions])

	useEffect(() => {
		if (!active_convention_id) return

		safeLocalStorageSetItem(storage_key, active_convention_id)
	}, [active_convention_id, storage_key])

	const convention = useMemo(() => {
		if (Array.isArray(conventions) && conventions.length > 0 && active_convention_id) {
			const found = conventions.find((c) => c?.id === active_convention_id)
			if (found) return found
		}

		return current_convention || {}
	}, [conventions, active_convention_id, current_convention])

	const setConventionId = useCallback((id) => {
		setSelectedConventionId(id)
	}, [])

	const setSeriesId = useCallback(
		(id) => {
			setSelectedSeriesId(id)
			if (allow_dev_overrides) setSelectedConventionId('')
		},
		[allow_dev_overrides],
	)

	useEffect(() => {
		applyThemeToDom(convention?.effective_theme)
	}, [
		convention?.effective_theme?.primary_color,
		convention?.effective_theme?.accent_color,
		convention?.effective_theme?.color_mode,
		convention?.effective_theme?.favicon_url,
	])

	useEffect(() => {
		if (typeof document === 'undefined') return

		const title = convention?.name || convention?.convention_series?.name
		if (!title) return
		document.title = title
	}, [convention?.id, convention?.name, convention?.convention_series?.id, convention?.convention_series?.name])

	const series = useMemo(() => {
		if (!active_series_id) return null
		if (!Array.isArray(convention_series) || convention_series.length === 0) return null
		return convention_series.find((s) => s?.id === active_series_id) || null
	}, [active_series_id, convention_series])

	const value = useMemo(
		() => ({
			convention,
			conventions,
			series,
			series_list: convention_series,
			loading: Boolean(loading_current || loading_conventions || loading_series),
			setConventionId,
			setSeriesId,
		}),
		[
			convention,
			conventions,
			series,
			convention_series,
			loading_current,
			loading_conventions,
			loading_series,
			setConventionId,
			setSeriesId,
		],
	)

	return <CurrentConventionContext.Provider value={value}>{children}</CurrentConventionContext.Provider>
}

CurrentConventionProvider.propTypes = {
	children: PropTypes.node.isRequired,
}

export default CurrentConventionProvider
export { CurrentConventionContext, useCurrentConvention }
